import type { ReactNode } from 'react';
import { Download, Loader2 } from 'lucide-react';
import styles from './ExportActions.module.css';

/**
 * Shared export action row — two buttons side-by-side, one for
 * CSV one for PDF. Consumed by:
 *   - PWA HistoryPage (driver self-export, async download helpers
 *     with blob handling + auth + toast)
 *   - admin ReportsPage (operator export, simple <a download>
 *     URL-based)
 *
 * Two APIs in one component to support both shapes:
 *   - `csvHref` / `pdfHref` — when set, button renders as a plain
 *     `<a download>` anchor. Server's Content-Disposition header
 *     drives the browser download. Simplest path; what admin uses.
 *   - `onCsv` / `onPdf` — async callback. Used when the consumer
 *     needs to compose the download client-side (PWA's
 *     auth-headered blob fetch + toast on completion).
 *
 * A button is disabled when:
 *   - Neither href nor callback is provided (the button is
 *     vestigial; consumer can leave the slot empty)
 *   - The per-format `*Busy` flag is true (in-flight callback)
 *   - The shared `disabled` flag is true (e.g., no rows match
 *     the filter, no permission)
 *
 * A button is HIDDEN entirely when its slot is `null` (vs
 * undefined) — consumer can opt-out of one format without the
 * other. The admin Reports surface currently hides the PDF
 * button (deferred to a future commit); just pass `pdfHref={null}`
 * or omit both pdf props.
 */
export interface ExportActionsProps {
  csvHref?: string | null;
  pdfHref?: string | null;
  onCsv?: () => void | Promise<void>;
  onPdf?: () => void | Promise<void>;
  csvBusy?: boolean;
  pdfBusy?: boolean;
  disabled?: boolean;
  csvLabel?: string;
  pdfLabel?: string;
  /** When provided, replaces the default Download icon for that
   *  button. Used by surfaces that want a domain-specific icon
   *  (e.g., a ledger-style icon for a receipts-bundle ZIP). */
  csvIcon?: ReactNode;
  pdfIcon?: ReactNode;
}

export function ExportActions({
  csvHref, pdfHref, onCsv, onPdf,
  csvBusy = false, pdfBusy = false,
  disabled = false,
  csvLabel = 'Export CSV',
  pdfLabel = 'Export PDF',
  csvIcon, pdfIcon,
}: ExportActionsProps) {
  // Hide vs disable: `null` = consumer explicitly opting out of
  // that button (hidden); `undefined` + no callback = no source
  // (rendered but disabled, so the layout slot stays). Most
  // consumers want hide-on-null behaviour for the format they
  // don't surface yet.
  const showCsv = !(csvHref === null && !onCsv);
  const showPdf = !(pdfHref === null && !onPdf);

  return (
    <div className={styles.row}>
      {showCsv && (
        <ExportButton
          href={csvHref ?? undefined}
          onClick={onCsv}
          busy={csvBusy}
          disabled={disabled || (!csvHref && !onCsv)}
          label={csvLabel}
          icon={csvIcon}
        />
      )}
      {showPdf && (
        <ExportButton
          href={pdfHref ?? undefined}
          onClick={onPdf}
          busy={pdfBusy}
          disabled={disabled || (!pdfHref && !onPdf)}
          label={pdfLabel}
          icon={pdfIcon}
        />
      )}
    </div>
  );
}

function ExportButton({
  href, onClick, busy, disabled, label, icon,
}: {
  href?: string;
  onClick?: () => void | Promise<void>;
  busy: boolean;
  disabled: boolean;
  label: string;
  icon?: ReactNode;
}) {
  const iconEl = busy
    ? <Loader2 size={14} className={styles.spin} aria-hidden />
    : (icon ?? <Download size={14} aria-hidden />);

  // Anchor path wins when href is set + no async callback. Anchor
  // gives the browser a native download (Content-Disposition on the
  // server picks the filename), no JS lifecycle. Callback path
  // covers async / auth-headered blob fetches.
  if (href && !onClick) {
    return (
      <a
        href={href}
        download
        className={`${styles.button} ${disabled ? styles.buttonDisabled : ''}`}
        aria-disabled={disabled || undefined}
        aria-label={label}
        // Stop the navigation when disabled at click time (the
        // disabled style is visual only; HTML <a> has no native
        // disabled attribute).
        onClick={disabled ? (e) => e.preventDefault() : undefined}
      >
        {iconEl}
        <span>{label}</span>
      </a>
    );
  }

  return (
    <button
      type="button"
      className={styles.button}
      disabled={disabled || busy}
      onClick={() => { void onClick?.(); }}
      aria-label={label}
    >
      {iconEl}
      <span>{label}</span>
    </button>
  );
}
